import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingBag, User, Menu, X, Heart, FlaskConical } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Button } from './ui/button';

const Navbar = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const { cartCount } = useCart();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <nav className="fixed top-0 left-0 right-0 bg-[#F5F2E8]/95 backdrop-blur-md border-b-2 border-[#4A7C29] px-6 py-4 flex items-center justify-between z-50 nav-bar">
      {/* Logo - Corporate Lockup */}
      <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
        <div className="w-9 h-9 bg-[#4A7C29] flex items-center justify-center">
          <FlaskConical className="w-5 h-5 text-white" />
        </div>
        <div className="hidden sm:block">
          <span className="font-semibold text-[#1A1A1A] text-sm tracking-[0.15em] font-['IBM_Plex_Sans_Condensed'] uppercase">TASTYJELLY</span>
          <span className="text-[9px] text-[#5A5A5A] block tracking-[0.2em] font-['IBM_Plex_Sans_Condensed'] uppercase">GLOBAL INDUSTRIES</span>
        </div>
      </Link>

      {/* Desktop Navigation */}
      <div className="hidden md:flex items-center gap-8">
        <Link 
          to="/products" 
          className="text-[#1A1A1A] text-xs font-medium tracking-[0.1em] uppercase font-['IBM_Plex_Sans_Condensed'] hover:text-[#4A7C29] transition-colors"
          data-testid="nav-products"
        >
          Drops
        </Link>
        <Link 
          to="/about" 
          className="text-[#1A1A1A] text-xs font-medium tracking-[0.1em] uppercase font-['IBM_Plex_Sans_Condensed'] hover:text-[#4A7C29] transition-colors"
          data-testid="nav-about"
        >
          About
        </Link>
      </div>

      {/* Right Side Icons */}
      <div className="flex items-center gap-2">
        {isAuthenticated && (
          <Link 
            to="/wishlist" 
            className="p-2 hover:bg-[#EBE7DB] transition-colors"
            data-testid="nav-wishlist"
          >
            <Heart className="w-5 h-5 text-[#1A1A1A]" />
          </Link>
        )}
        
        <Link 
          to="/cart" 
          className="p-2 hover:bg-[#EBE7DB] transition-colors relative"
          data-testid="nav-cart"
        >
          <ShoppingBag className="w-5 h-5 text-[#1A1A1A]" />
          {cartCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-[#4A7C29] text-white text-xs font-bold w-5 h-5 flex items-center justify-center cart-badge-animate">
              {cartCount}
            </span>
          )}
        </Link>

        {isAuthenticated ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                className="p-2 hover:bg-[#EBE7DB]"
                data-testid="nav-user-menu"
              >
                <User className="w-5 h-5 text-[#1A1A1A]" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48 rounded-none border-2 border-[#4A7C29]">
              <div className="px-3 py-2 text-xs text-[#5A5A5A] font-['IBM_Plex_Sans_Condensed'] uppercase tracking-wider">
                {user?.name}
              </div>
              <DropdownMenuSeparator className="bg-[#EBE7DB]" />
              <DropdownMenuItem asChild className="font-['IBM_Plex_Sans'] text-sm">
                <Link to="/account" data-testid="nav-account">Account</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild className="font-['IBM_Plex_Sans'] text-sm">
                <Link to="/orders" data-testid="nav-orders">Orders</Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-[#EBE7DB]" />
              <DropdownMenuItem 
                onClick={handleLogout}
                className="text-[#1A1A1A] font-['IBM_Plex_Sans'] text-sm"
                data-testid="nav-logout"
              >
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <Link to="/login">
            <Button 
              className="bg-[#4A7C29] text-white font-semibold px-5 py-2 text-xs tracking-[0.1em] uppercase font-['IBM_Plex_Sans_Condensed'] hover:bg-[#3D6822] transition-colors rounded-none"
              data-testid="nav-login"
            >
              Sign In
            </Button>
          </Link>
        )}

        {/* Mobile Menu Button */}
        <button 
          className="md:hidden p-2"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          data-testid="nav-mobile-toggle"
        >
          {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="absolute top-full left-0 right-0 bg-[#F5F2E8] border-b-2 border-[#4A7C29] p-4 md:hidden">
          <div className="flex flex-col gap-1">
            <Link 
              to="/products" 
              className="px-4 py-3 hover:bg-[#EBE7DB] font-medium text-xs tracking-[0.1em] uppercase font-['IBM_Plex_Sans_Condensed']"
              onClick={() => setMobileMenuOpen(false)}
            >
              Drops
            </Link>
            <Link 
              to="/about" 
              className="px-4 py-3 hover:bg-[#EBE7DB] font-medium text-xs tracking-[0.1em] uppercase font-['IBM_Plex_Sans_Condensed']"
              onClick={() => setMobileMenuOpen(false)}
            >
              About
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
